import React, { useState } from 'react';
import { Flame } from 'lucide-react';
import { formatCOP } from '../utils/kpiFormatters';
import type { KpiHeatmapCell } from '../types';

interface Props {
  data: {
    days: string[];
    cells: KpiHeatmapCell[];
  };
}

export const HourHeatmap: React.FC<Props> = ({ data }) => {
  const [hovered, setHovered] = useState<KpiHeatmapCell | null>(null);

  if (!data || !data.cells || data.cells.length === 0) {
    return <div className="py-8 text-center text-xs text-text-muted">Sin datos de horas pico</div>;
  }

  const maxRevenue = Math.max(...data.cells.map((c) => c.revenue), 1);

  // Mapear celdas por clave "dia_hora"
  const cellMap = new Map<string, KpiHeatmapCell>();
  data.cells.forEach((c) => cellMap.set(`${c.day_index}_${c.hour}`, c));

  // Encontrar la hora pico absoluta
  const peakCell = [...data.cells].sort((a, b) => b.revenue - a.revenue)[0];

  const getColorClass = (rev: number) => {
    if (rev <= 0) return 'bg-muted/40 hover:border-slate-400';
    const ratio = rev / maxRevenue;
    if (ratio < 0.25) return 'bg-emerald-100 dark:bg-emerald-950/60 border-emerald-200';
    if (ratio < 0.5) return 'bg-emerald-300 dark:bg-emerald-800 border-emerald-400';
    if (ratio < 0.75) return 'bg-emerald-500 dark:bg-emerald-600 border-emerald-600 text-white';
    return 'bg-emerald-600 dark:bg-emerald-500 border-emerald-700 text-white font-bold';
  };

  return (
    <div className="space-y-3">
      {/* Banner de hora pico destacada */}
      {peakCell && peakCell.revenue > 0 && (
        <div className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 text-xs">
          <Flame size={16} className="text-amber-600 shrink-0" />
          <span className="text-text">
            <strong>Franja de mayor facturación:</strong> {peakCell.day_name} a las{' '}
            {sprintfHour(peakCell.hour)} con <strong>{peakCell.orders} pedidos</strong> (
            {formatCOP(peakCell.revenue)})
          </span>
        </div>
      )}

      {/* Grid del Mapa de Calor */}
      <div className="overflow-x-auto pb-2">
        <div className="min-w-[640px]">
          {/* Cabecera de Horas (0 a 23) */}
          <div className="flex items-center text-[10px] text-text-muted font-bold mb-1 pl-16">
            {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
              <div key={h} className="w-[12.5%] text-left">
                {sprintfHour(h)}
              </div>
            ))}
          </div>

          {/* Filas de Días */}
          <div className="space-y-1">
            {data.days.map((dayName, dIdx) => (
              <div key={dayName} className="flex items-center gap-1">
                <span className="w-16 text-[11px] font-semibold text-text-muted truncate">
                  {dayName.slice(0, 3)}
                </span>

                <div className="flex-1 grid grid-cols-24 gap-1">
                  {Array.from({ length: 24 }).map((_, h) => {
                    const cell = cellMap.get(`${dIdx}_${h}`) || {
                      day_index: dIdx,
                      day_name: dayName,
                      hour: h,
                      revenue: 0,
                      orders: 0,
                    };

                    return (
                      <div
                        key={h}
                        onMouseEnter={() => setHovered(cell)}
                        onMouseLeave={() => setHovered(null)}
                        className={`h-5 rounded-[4px] border border-transparent transition cursor-pointer ${getColorClass(
                          cell.revenue
                        )}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip flotante con detalles */}
      <div className="h-6 text-xs text-text-muted flex items-center justify-between">
        {hovered ? (
          <span className="text-text font-semibold">
            📍 {hovered.day_name} a las {sprintfHour(hovered.hour)}:{' '}
            <strong className="text-emerald-600">{formatCOP(hovered.revenue)}</strong> en{' '}
            {hovered.orders} pedidos
          </span>
        ) : (
          <span className="text-[11px]">Pasa el cursor sobre un bloque para ver detalles</span>
        )}

        <div className="flex items-center gap-1.5 text-[10px] text-text-muted">
          <span>Menos ventas</span>
          <div className="flex gap-0.5">
            <span className="w-2.5 h-2.5 rounded-xs bg-muted" />
            <span className="w-2.5 h-2.5 rounded-xs bg-emerald-200" />
            <span className="w-2.5 h-2.5 rounded-xs bg-emerald-400" />
            <span className="w-2.5 h-2.5 rounded-xs bg-emerald-600" />
          </div>
          <span>Más ventas</span>
        </div>
      </div>
    </div>
  );
};

function sprintfHour(h: number): string {
  return `${h.toString().padStart(2, '0')}:00`;
}
